import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Badge,
  Alert,
  AlertDescription
} from '../../components/ui';
import {
  MapPin,
  CheckCircle,
  AlertTriangle,
  FileText,
  Eye,
  EyeOff
} from 'lucide-react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const API_URL = import.meta.env.VITE_API_URL;

const AddressValidationSection = ({ shipmentLabels, confirmedAddresses, onAddressConfirm, onComplete }) => {
  const [shipmentAddresses, setShipmentAddresses] = useState({});
  const [adjustedAddresses, setAdjustedAddresses] = useState({});
  const [collapsedLabels, setCollapsedLabels] = useState(new Set());

  // Initialize addresses on mount
  useEffect(() => {
    if (shipmentLabels.length > 0) {
      fetchAdjustedAddresses();
    }
  }, [shipmentLabels]);

  // Fetch adjusted addresses from database
  const fetchAdjustedAddresses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/warehouse-addresses`);
      const data = await response.json();
      const adjustedAddressesMap = {};

      data.forEach(address => {
        adjustedAddressesMap[address.warehouse_id] = address;
      });

      // Initialize shipment addresses with adjusted or original addresses
      const updatedAddresses = {};
      shipmentLabels.forEach(shipment => {
        const adjustedAddress = adjustedAddressesMap[shipment.warehouseId];
        if (adjustedAddress) {
          // Use adjusted address from database
          updatedAddresses[shipment.shipmentId] = {
            address: adjustedAddress.address_line_1,
            city: adjustedAddress.city,
            state: adjustedAddress.state_or_province_code,
            zip: adjustedAddress.postal_code,
          };
        } else {
          // Use original address from API
          updatedAddresses[shipment.shipmentId] = {
            address: shipment.address.address_line_1 || '',
            city: shipment.address.city || '',
            state: shipment.address.state_or_province_code || '',
            zip: shipment.address.postal_code || '',
          };
        }
      });

      setShipmentAddresses(updatedAddresses);
      setAdjustedAddresses(adjustedAddressesMap);
    } catch (error) {
      console.error('Error fetching adjusted addresses:', error);
    }
  };

  // Handle address field changes
  const handleAddressChange = (shipmentId, field, value) => {
    setShipmentAddresses(prev => ({
      ...prev,
      [shipmentId]: {
        ...prev[shipmentId],
        [field]: value,
      },
    }));
  };

  // Handle shipment confirmation
  const handleConfirmShipment = async (shipmentId, warehouseId, originalAddress) => {
    const address = shipmentAddresses[shipmentId];

    // Check if address has been edited
    const isAddressEdited = (
      address.address !== originalAddress.address_line_1 ||
      address.city !== originalAddress.city ||
      address.state !== originalAddress.state_or_province_code ||
      address.zip !== originalAddress.postal_code
    );

    if (isAddressEdited) {
      try {
        await fetch(`${API_URL}/api/warehouse-addresses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            warehouse_id: warehouseId,
            address_line_1: address.address,
            city: address.city,
            state_or_province_code: address.state,
            postal_code: address.zip,
            is_adjusted: true,
          }),
        });
      } catch (error) {
        console.error('Error saving adjusted address:', error);
        return;
      }
    }

    onAddressConfirm(shipmentId);
  };

  // Toggle label visibility
  const toggleLabelVisibility = (shipmentId) => {
    setCollapsedLabels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shipmentId)) {
        newSet.delete(shipmentId);
      } else {
        newSet.add(shipmentId);
      }
      return newSet;
    });
  };

  const allAddressesConfirmed = shipmentLabels.length > 0 && 
    shipmentLabels.every(shipment => confirmedAddresses.has(shipment.shipmentId));

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="text-lg font-medium">Address Validation</h3>
                <p className="text-sm text-slate-600">
                  Review shipping labels and confirm addresses. Human validation required for accuracy.
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Badge variant="outline" className="text-sm">
                {confirmedAddresses.size} of {shipmentLabels.length} confirmed
              </Badge>
              
              {allAddressesConfirmed && (
                <Badge variant="default" className="text-sm bg-green-100 text-green-700">
                  ✓ All Addresses Confirmed
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Progress Bar */}
      {shipmentLabels.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Validation Progress</span>
                <span className="font-medium">
                  {confirmedAddresses.size} / {shipmentLabels.length}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(confirmedAddresses.size / shipmentLabels.length) * 100}%` 
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shipment Labels */}
      {shipmentLabels.length > 0 ? (
        <div className="space-y-4">
          {shipmentLabels.map((shipment, index) => {
            const isConfirmed = confirmedAddresses.has(shipment.shipmentId);
            const isCollapsed = collapsedLabels.has(shipment.shipmentId);
            const adjustedAddress = adjustedAddresses[shipment.warehouseId];
            const isAdjusted = adjustedAddress && adjustedAddress.is_adjusted;

            return (
              <Card key={shipment.shipmentId} className={`${
                isConfirmed ? 'border-green-200 bg-green-50' : 'border-slate-200'
              }`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Badge variant="secondary" className="text-xs">
                        #{index + 1}
                      </Badge>
                      <h4 className="text-lg font-medium">
                        Shipment ID: {shipment.shipmentId}
                      </h4>
                      {isAdjusted && (
                        <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                          Adjusted Address
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        onClick={() => toggleLabelVisibility(shipment.shipmentId)}
                        variant="outline"
                        size="sm"
                      >
                        {isCollapsed ? (
                          <>
                            <Eye className="w-4 h-4 mr-2" />
                            Show Label
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4 mr-2" />
                            Hide Label
                          </>
                        )}
                      </Button>
                      
                      {isConfirmed && (
                        <Badge variant="default" className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Confirmed
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {!isCollapsed && (
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* PDF Viewer Section - No Auto-Print */}
                      <div>
                        <h5 className="text-sm font-medium mb-3 flex items-center space-x-2">
                          <FileText className="w-4 h-4" />
                          <span>Shipping Label</span>
                        </h5>
                        {shipment.url ? (
                          <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
                              <div style={{ height: '400px', width: '100%' }}>
                                <Viewer
                                  fileUrl={`${API_URL}/api/pdf-proxy?url=${encodeURIComponent(shipment.url)}&firstPageOnly=true`}
                                  plugins={[
                                    defaultLayoutPlugin({
                                      sidebarTabs: () => [], // Hide sidebar
                                      toolbar: {
                                        searchPlugin: false, // Hide search
                                        fullScreenPlugin: false, // Hide fullscreen
                                        openPlugin: false, // Hide open file
                                        printPlugin: false, // Hide print
                                        propertiesPlugin: false, // Hide properties
                                        rotatePlugin: false, // Hide rotate
                                        scrollModePlugin: false, // Hide scroll mode
                                        selectionModePlugin: false, // Hide selection mode
                                        themePlugin: false, // Hide theme
                                        moreActionsPopover: false, // Hide more actions
                                        pageNavigationPlugin: false, // Hide page navigation since it's single page
                                      }
                                    })
                                  ]}
                                />
                              </div>
                            </Worker>
                          </div>
                        ) : (
                          <Alert className="border-red-200 bg-red-50">
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                            <AlertDescription className="text-red-700">
                              No shipping label found for this shipment.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>

                      {/* Address Form Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-medium flex items-center space-x-2">
                            <MapPin className="w-4 h-4" />
                            <span>Destination Address</span>
                          </h5>
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Address Line 1 *
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={shipmentAddresses[shipment.shipmentId]?.address || ''}
                              onChange={(e) => handleAddressChange(shipment.shipmentId, 'address', e.target.value)}
                              disabled={isConfirmed}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                City *
                              </label>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={shipmentAddresses[shipment.shipmentId]?.city || ''}
                                onChange={(e) => handleAddressChange(shipment.shipmentId, 'city', e.target.value)}
                                disabled={isConfirmed}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                State *
                              </label>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={shipmentAddresses[shipment.shipmentId]?.state || ''}
                                onChange={(e) => handleAddressChange(shipment.shipmentId, 'state', e.target.value)}
                                disabled={isConfirmed}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              ZIP Code *
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={shipmentAddresses[shipment.shipmentId]?.zip || ''}
                              onChange={(e) => handleAddressChange(shipment.shipmentId, 'zip', e.target.value)}
                              disabled={isConfirmed}
                            />
                          </div>
                        </div>

                        {/* Confirmation Section */}
                        <div className="mt-6 pt-4 border-t border-slate-200">
                          {isConfirmed ? (
                            <div className="flex items-center space-x-2 text-green-600">
                              <CheckCircle className="w-5 h-5" />
                              <span className="font-medium">Address Confirmed</span>
                            </div>
                          ) : (
                            <Button
                              onClick={() => handleConfirmShipment(shipment.shipmentId, shipment.warehouseId, shipment.address)}
                              className="bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>Confirm Address</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500">No shipment labels found</p>
            <p className="text-sm text-slate-400 mt-1">
              Labels will appear here when shipments are ready
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {shipmentLabels.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {allAddressesConfirmed ? (
                  <span className="text-green-600 font-medium">
                    ✓ All addresses have been validated and confirmed
                  </span>
                ) : (
                  <span>
                    Please review and confirm all shipping addresses above
                  </span>
                )}
              </div>
              
              <Button
                onClick={onComplete}
                disabled={!allAddressesConfirmed}
                className={`flex items-center space-x-2 ${
                  allAddressesConfirmed 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-slate-400 cursor-not-allowed'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                <span>All Addresses Confirmed - Continue</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AddressValidationSection; 